import {inject, Injectable} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TranslateService} from "@ngx-translate/core";
import {MonoTypeOperatorFunction, Observable, Subject, tap} from "rxjs";
import {environment} from "../../../environments/environment";
import {AuthenticationService} from "../auth/authentication.service";

/** The messenger loaded by the Intercom snippet (index.html), absent until it is ready. */
type IntercomWindow = Window & {Intercom?: (...args: unknown[]) => void};

@Injectable({
	providedIn: 'root'
})
export class IntercomService {
	private authenticationService = inject(AuthenticationService);
	private translateService = inject(TranslateService);
	private intercomEvents = new Subject<'hide' | 'show'>()
	private lastIntercomState: 'show' | 'hide' = 'hide';

	constructor() {
		this.waitForIntercom(() => {
			this.intercom('onShow', () => this.intercomEvents.next('show'));
			this.intercom('onHide', () => this.intercomEvents.next('hide'));
		});

		this.intercomEvents.pipe(
			tap(event => this.lastIntercomState = event)
		).subscribe();

		// The messenger, and the Help Center articles it shows, follow the app language rather than the browser's.
		this.translateService.onLangChange
			.pipe(takeUntilDestroyed())
			.subscribe(({lang}) => this.intercom('update', {language_override: lang}));
	}

	private intercom(...args: unknown[]): void {
		(window as IntercomWindow).Intercom?.(...args);
	}

	private waitForIntercom(fn: () => void): void {
		if (typeof (window as IntercomWindow).Intercom === 'function') {
			fn();
		} else {
			const check = setInterval(() => {
				if (typeof (window as IntercomWindow).Intercom === 'function') {
					clearInterval(check);
					fn();
				}
			}, 500);
		}
	}

	boot() : void {
		const userInfo = this.authenticationService.getUserInfo();
		if (userInfo) {
			const settings =  {
				app_id: environment.intercomAppId,
				name: userInfo.firstName + ' ' + userInfo.lastName,
				email: userInfo.email,
				user_id: userInfo.id,
				hide_default_launcher: true,
				language_override: this.translateService.getCurrentLang(),
			};
			this.intercom('boot', settings);
		} else {
			const settings = {
				app_id: environment.intercomAppId,
				language_override: this.translateService.getCurrentLang(),
			};

			this.intercom('boot', settings);
		}
	}

	show(): void {
		this.intercom('show');
	}

	/** Opens the messenger on a new conversation, its composer prefilled with `content` (still editable). */
	showNewMessage(content: string): void {
		this.intercom('showNewMessage', content);
	}

	showArticle(id: string): void {
		this.intercom('showArticle', id);
	}

	hide(): void {
		this.intercom('hide');
	}

	toggle(): void {
		this.intercom(this.lastIntercomState === 'show' ? 'hide' : 'show');
	}

	update(): void {
		this.intercom('update', {});
	}

	onEvent(): Observable<'hide' | 'show'> {
		return this.intercomEvents.asObservable();
	}
}

export function updateIntercom<T>(intercomService: IntercomService): MonoTypeOperatorFunction<T> {
	return (source: Observable<T>) => source.pipe(
		tap(() => intercomService.update())
	);
}

